// pms-adapters / supabase / proof — the PMS proof, read and classed
// (PMS-PROOF-BRIEF §5, §10; migration 20261012100200).
//
// Two RPCs, `pms_proof_read` and `pms_proof_classify`, both operator-only. A
// missing capability is `forbidden`, never an empty proof: « no day closed yet »
// and « you may not look » must not read the same.

import {
  err,
  ok,
  type IsoDate,
  type PmsProof,
  type ProofBackfill,
  type ProofClass,
  type ProofDay,
  type ProofDifference,
  type ProofRange,
  type PropertyCode,
  type Result,
} from '@lemontaigu/pms-core';
import type { PostgrestError } from '@supabase/supabase-js';
import { toPmsError, type SupabaseClient } from './support.js';

/** The RPC's jsonb, verbatim (already camelCase: built by the function). */
export interface ProofReadJson {
  property: string;
  currentDay: string | null;
  vegaWorkingDay: string | null;
  vegaReadAt: string | null;
  lastBackfill?: {
    id: number;
    fromDay: string;
    status: string;
    requestedAt: string;
    finishedAt: string | null;
    error: string | null;
    bookingsSeen: number | null;
    bookingsInserted: number | null;
    bookingsRefused: number | null;
    daysReplayed: number | null;
  } | null;
  days: Array<{
    closedDay: string;
    origin?: string;
    closedAt: string;
    mode: string;
    unresolvedArrivals: number;
    caughtUp: boolean;
    vegaImportId: number | null;
    figures: { pms?: Record<string, number> | null; vega?: Record<string, number> | null } | null;
    unclassified: number;
    clean: boolean;
    differences: Array<{
      id: number;
      metric: string;
      subject: string;
      pms: number;
      vega: number;
      class: string | null;
      note: string | null;
      clientName?: string | null;
      clientRef?: string | null;
      groupName?: string | null;
    }>;
  }>;
}

const CLASSES: readonly ProofClass[] = ['M', 'P', 'V', 'R'];
const BACKFILL_STATUSES: readonly ProofBackfill['status'][] = ['pending', 'done', 'failed'];

/** Maps the reply. Returns null when it is not a reply this build can read —
 *  including a class it does not know, which must not show as « unclassed ». */
export function toProofRange(j: ProofReadJson | null): ProofRange | null {
  if (!j || typeof j !== 'object' || !Array.isArray(j.days)) return null;
  let lastBackfill: ProofBackfill | null = null;
  if (j.lastBackfill) {
    const status = BACKFILL_STATUSES.find((s) => s === j.lastBackfill!.status);
    if (!status) return null;
    lastBackfill = { ...j.lastBackfill, fromDay: j.lastBackfill.fromDay as IsoDate, status };
  }
  const days: ProofDay[] = [];
  for (const d of j.days) {
    if (!Array.isArray(d.differences)) return null;
    // A row from before PR0b carries no origin: it was a close.
    const origin = d.origin === undefined ? 'close' : d.origin === 'close' || d.origin === 'replay' ? d.origin : null;
    if (!origin) return null;
    const differences: ProofDifference[] = [];
    for (const x of d.differences) {
      const cls = x.class === null ? null : CLASSES.find((c) => c === x.class);
      if (cls === undefined) return null;
      differences.push({
        id: x.id,
        metric: x.metric,
        subject: x.subject,
        pms: x.pms,
        vega: x.vega,
        class: cls,
        note: x.note,
        clientName: x.clientName ?? null,
        clientRef: x.clientRef ?? null,
        groupName: x.groupName ?? null,
      });
    }
    days.push({
      closedDay: d.closedDay as IsoDate,
      origin,
      closedAt: d.closedAt,
      mode: d.mode,
      unresolvedArrivals: d.unresolvedArrivals,
      caughtUp: d.caughtUp,
      vegaImportId: d.vegaImportId,
      figures: { pms: d.figures?.pms ?? {}, vega: d.figures?.vega ?? {} },
      unclassified: d.unclassified,
      clean: d.clean,
      differences,
    });
  }
  return {
    currentDay: j.currentDay as IsoDate | null,
    vegaWorkingDay: j.vegaWorkingDay as IsoDate | null,
    vegaReadAt: j.vegaReadAt,
    lastBackfill,
    days,
  };
}

/** The function's own refusals, without its name in front. */
function refusal(e: PostgrestError, what: string): Result<never> {
  if (e.code === '42501') return err('forbidden', `Accès refusé — ${what}`, { cause: e });
  if (e.code === 'P0001') {
    return err('validation', e.message.replace(/^pms_proof_\w+:\s*/, ''), { cause: e });
  }
  return { ok: false, error: toPmsError(e) };
}

export class SupabasePmsProof implements PmsProof {
  constructor(private readonly db: SupabaseClient) {}

  async read(propertyCode: PropertyCode, from: IsoDate, to: IsoDate): Promise<Result<ProofRange>> {
    if (from > to) return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    const r = await this.db.rpc('pms_proof_read', { p_property: propertyCode, p_from: from, p_to: to });
    if (r.error) return refusal(r.error, 'preuve PMS');
    const range = toProofRange(r.data as ProofReadJson | null);
    // An older build than the database: refuse rather than show less.
    if (!range) return err('unknown', 'Preuve PMS : réponse illisible — recharger la page', { retryable: false });
    return ok(range);
  }

  async classify(differenceId: number, cls: ProofClass | null, note: string | null): Promise<Result<void>> {
    const r = await this.db.rpc('pms_proof_classify', {
      p_difference_id: differenceId,
      p_class: cls,
      p_note: note,
    });
    if (r.error) return refusal(r.error, 'classement des écarts');
    return ok(undefined);
  }

  async requestBackfill(propertyCode: PropertyCode, from: IsoDate): Promise<Result<number>> {
    const r = await this.db.rpc('pms_proof_backfill_request', { p_property: propertyCode, p_from: from });
    if (r.error) return refusal(r.error, 'reconstitution de la preuve');
    if (typeof r.data !== 'number') {
      return err('unknown', 'Reconstitution : réponse illisible', { retryable: false });
    }
    return ok(r.data);
  }
}
